import { useEffect, useState, useCallback, useId, useRef } from 'react';
import Layout from '@/components/Layout';
import MiddleEastMap from '@/components/MiddleEastMap';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import esgSampleReport from "@assets/optimized/esg-sample-report-1280.webp";
import esgSampleReport640 from "@assets/optimized/esg-sample-report-640.webp";
import companyRatingsDashboard from "@assets/optimized/company-esg-ratings-dashboard-1280.webp";
import companyRatingsDashboard640 from "@assets/optimized/company-esg-ratings-dashboard-640.webp";
import benchmarkingScreenshot from "@assets/optimized/benchmarking-dashboard-1280.webp";
import benchmarkingScreenshot640 from "@assets/optimized/benchmarking-dashboard-640.webp";
import portfolioManagementScreenshot from "@assets/optimized/portfolio-management-dashboard-1280.webp";
import portfolioManagementScreenshot640 from "@assets/optimized/portfolio-management-dashboard-640.webp";
import kpisScreenshot from "@assets/optimized/esg-kpis-dashboard-1280.webp";
import kpisScreenshot640 from "@assets/optimized/esg-kpis-dashboard-640.webp";
import excelExportScreenshot from "@assets/optimized/esg-excel-export-1280.webp";
import excelExportScreenshot640 from "@assets/optimized/esg-excel-export-640.webp";
import { 
  BarChart3, 
  Briefcase, 
  TrendingUp, 
  FileText, 
  Target, 
  ShoppingCart,
  CheckCircle, 
  Shield, 
  Zap, 
  Globe2, 
  Users,
  Building2,
  MapPin,
  Trophy,
  Download,
  Wrench,
  X,
  Database,
  Activity,
  CalendarDays,
  Landmark,
  Search,
  Package,
  Vote,
  Cpu
} from "lucide-react";
import { Link } from "wouter";
import { useTranslations } from "@/lib/translations";
import SubscriptionCards from "@/components/SubscriptionCards";
import { usePageMeta, SITE_URL } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { pushEvent } from "@/lib/analytics";

const topFiveCompanies = [
  { name: 'ADNOC Distribution', score: 86, environmental: 89, social: 83, governance: 87 },
  { name: 'Saudi Electricity Company', score: 83, environmental: 77, social: 76, governance: 80 },
  { name: 'Saudi Telecom Co.', score: 83, environmental: 72, social: 73, governance: 82 },
  { name: 'Qatar National Bank', score: 81, environmental: 80, social: 70, governance: 74 },
  { name: 'Aldar Properties PJSC', score: 80, environmental: 80, social: 88, governance: 53 },
];

const GaugeChart = ({ value, label, color, size = 'sm', className }: { value: number; label: string; color: string; size?: 'sm' | 'lg'; className?: string }) => {
  const rawId = useId().replace(/[:]/g, '');
  const safe = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
  const radius = 42;
  const strokeWidth = size === 'lg' ? 8 : 7;
  const circumference = 2 * Math.PI * radius;
  const progress = (safe / 100) * circumference;
  const dashOffset = circumference - progress;
  const svgW = size === 'lg' ? 152 : 104;
  const lighter = `color-mix(in srgb, ${color} 55%, #ffffff)`;

  return (
    <div className={`flex flex-col items-center w-full ${className ?? ''}`}>
      <svg viewBox="0 0 100 100" overflow="visible" preserveAspectRatio="xMidYMid meet" className="h-auto w-full" style={{ maxWidth: svgW }} role="img" aria-label={`${label}: ${safe} out of 100`}>
        <defs>
          <linearGradient id={`gauge-grad-${rawId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={lighter} />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
          <filter id={`gauge-glow-${rawId}`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.4" floodColor={color} floodOpacity="0.25" />
          </filter>
        </defs>
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        <circle
          className="gauge-arc"
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={`url(#gauge-grad-${rawId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 50 50)"
          filter={`url(#gauge-glow-${rawId})`}
          style={{ ['--g-from' as string]: `${circumference}`, ['--g-to' as string]: `${dashOffset}` } as React.CSSProperties}
        />
        <text
          x="50"
          y={size === 'lg' ? 45 : 44}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-foreground tabular-nums"
          fontSize={size === 'lg' ? '30' : '27'}
          fontWeight="800"
          style={{ letterSpacing: '-0.04em' }}
        >
          {safe}
        </text>
        <text
          x="50"
          y={size === 'lg' ? 63 : 62}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-muted-foreground"
          fontSize={size === 'lg' ? '8.5' : '8'}
          fontWeight="600"
          opacity="0.55"
        >
          / 100
        </text>
      </svg>
      <span className={`mt-2 font-semibold tracking-tight ${size === 'lg' ? 'text-sm' : 'text-xs text-muted-foreground'}`} style={size === 'lg' ? { color } : undefined}>{label}</span>
    </div>
  );
};

const CountUp = ({ value, className, testId, duration = 1800 }: { value: string; className?: string; testId?: string; duration?: number }) => {
  const match = value.match(/^(\D*)([\d.,]+)(.*)$/);
  const prefix = match?.[1] ?? '';
  const numStr = match?.[2] ?? '';
  const suffix = match?.[3] ?? '';
  const target = parseFloat(numStr.replace(/,/g, ''));
  const hasComma = numStr.includes(',');
  const isValid = match !== null && !Number.isNaN(target);

  const [display, setDisplay] = useState(value);
  const ref = useRef<HTMLDivElement>(null);
  const rafId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isValid) return;
    const el = ref.current;
    if (!el) return;

    const format = (n: number) => {
      const rounded = Math.round(n);
      const str = hasComma ? rounded.toLocaleString('en-US') : String(rounded);
      return `${prefix}${str}${suffix}`;
    };

    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(format(target));
      return;
    }

    let started = false;
    const animate = () => {
      if (started) return;
      started = true;
      const start = performance.now();
      setDisplay(format(0));
      const tick = (now: number) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(format(progress < 1 ? target * eased : target));
        if (progress < 1) rafId.current = requestAnimationFrame(tick);
      };
      rafId.current = requestAnimationFrame(tick);
    };

    if (typeof IntersectionObserver === 'undefined') {
      animate();
      return () => {
        if (rafId.current !== undefined) cancelAnimationFrame(rafId.current);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (rafId.current !== undefined) cancelAnimationFrame(rafId.current);
    };
  }, [value, isValid, target, prefix, suffix, hasComma, duration]);

  return (
    <div ref={ref} className={className} data-testid={testId}>
      {display}
    </div>
  );
};

const TopFiveSection = ({ t, isRTL }: { t: (key: string) => string; isRTL: boolean }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = topFiveCompanies[selectedIndex];

  return (
    <section className="py-16 bg-se-gray-50 dark:bg-background" data-testid="section-top5">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            {t('top5.title')}
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <Card className="p-6 bg-white dark:bg-card border-0 shadow-premium flex flex-col" data-testid="card-top5-detail">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">{t('topFive.performer')}</h3>
              <span className="text-xs text-muted-foreground/60 font-medium">{t('topFive.scoreOutOf')}</span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="flex items-center gap-3 mb-6 justify-center">
                <Trophy className="h-8 w-8 text-accent" />
                <h3 className="text-xl font-bold text-foreground" dir="ltr">{selected.name}</h3>
              </div>
              {selected.environmental > 0 ? (
                <div className="flex flex-col items-center gap-4 w-full">
                  <GaugeChart value={selected.score} label={t('top5.overallScore')} color="#d09310" size="lg" />
                  <div className={`flex w-full justify-center gap-3 sm:gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <GaugeChart value={selected.environmental} label={t('top5.environmental')} color="#1a7a4c" className="flex-1 min-w-0 max-w-[140px]" />
                    <GaugeChart value={selected.social} label={t('top5.social')} color="#2563eb" className="flex-1 min-w-0 max-w-[140px]" />
                    <GaugeChart value={selected.governance} label={t('top5.governance')} color="#7c3aed" className="flex-1 min-w-0 max-w-[140px]" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <p className="text-muted-foreground text-sm italic">ESG breakdown available for top-ranked company</p>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6 bg-white dark:bg-card border-0 shadow-premium flex flex-col" data-testid="card-top5-ranking">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">{t('topFive.rankings')}</h3>
              <span className="text-xs text-muted-foreground/60 font-medium">{t('topFive.scoreOutOf')}</span>
            </div>
            <div className="flex-1 flex flex-col justify-between gap-5">
              {topFiveCompanies.map((company, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  className={`w-full text-left ${isRTL ? 'text-right' : ''}`}
                  data-testid={`button-top5-company-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${selectedIndex === index ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'}`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0" dir="ltr">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground truncate mr-2">
                          {company.name}
                        </span>
                        <span className="text-sm font-bold text-se-green flex-shrink-0">{company.score}</span>
                      </div>
                      <div className="w-full bg-muted/70 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-2 rounded-full transition-all duration-700"
                          style={{
                            width: `${company.score}%`,
                            background: 'linear-gradient(90deg, hsl(var(--se-green)), color-mix(in srgb, hsl(var(--se-green)) 60%, #ffffff))',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground/60 italic mt-6 text-center">2025 Rankings</p>
          </Card>
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  const { t, isRTL } = useTranslations();
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  const openLightbox = useCallback((src: string, alt: string) => {
    setLightboxImage(src);
    setLightboxAlt(alt);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
    setLightboxAlt('');
    document.body.style.overflow = '';
  }, []);
  
  usePageMeta({
    title: "ESG Ratings for Middle East Investors | ESG Invest",
    description:
      "Access ESG ratings for 880+ listed companies across Saudi Arabia, UAE, Qatar & the Middle East. Independent ratings, portfolio tools & analytics for institutional investors.",
    path: "/",
  });

  useJsonLd("ld-home-service", {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#service`,
    name: "ESG Invest — Middle East ESG Ratings",
    url: `${SITE_URL}/`,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: [
      "Saudi Arabia",
      "United Arab Emirates",
      "Qatar",
      "Kuwait",
      "Bahrain",
      "Oman",
      "Jordan",
      "Lebanon",
      "Palestine",
      "Egypt",
    ],
    serviceType: "ESG ratings and investor analytics",
    description:
      "Independent ESG ratings, portfolio analytics, screening, and reporting tools covering 880+ listed companies across the Middle East.",
    audience: { "@type": "Audience", audienceType: "Institutional investors" },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "ESG Invest subscriptions",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "ESG Ratings Platform Access",
            description: "Full access to ESG ratings, screening, and portfolio analytics across Middle East markets.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Individual ESG Company Report",
            description: "One-off ESG report for a single Middle East listed company.",
          },
          price: "100",
          priceCurrency: "USD",
        },
      ],
    },
  });

  useEffect(() => {
    if (!lightboxImage) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxImage, closeLightbox]);

  const featuresList = [
    {
      icon: BarChart3,
      pillKey: 'pill.ratings',
      titleKey: 'featuresPage.feature1.title',
      descKey: 'featuresPage.feature1.desc',
      highlights: ['feature.highlight1.1', 'feature.highlight1.2', 'feature.highlight1.3'],
      reverse: false,
      image: companyRatingsDashboard,
      image640: companyRatingsDashboard640,
      width: 1280,
      height: 1139
    },
    {
      icon: Target,
      pillKey: 'pill.benchmarking',
      titleKey: 'featuresPage.feature2.title',
      descKey: 'featuresPage.feature2.desc',
      highlights: ['feature.highlight2.1', 'feature.highlight2.2', 'feature.highlight2.3'],
      reverse: true,
      image: benchmarkingScreenshot,
      image640: benchmarkingScreenshot640,
      width: 1280,
      height: 881
    },
    {
      icon: Briefcase,
      pillKey: 'pill.portfolio',
      titleKey: 'featuresPage.feature3.title',
      descKey: 'featuresPage.feature3.desc',
      highlights: ['feature.highlight3.1', 'feature.highlight3.2', 'feature.highlight3.3'],
      reverse: false,
      image: portfolioManagementScreenshot,
      image640: portfolioManagementScreenshot640,
      width: 1280,
      height: 539
    },
    {
      icon: FileText,
      pillKey: 'pill.reports',
      titleKey: 'featuresPage.feature4.title',
      descKey: 'featuresPage.feature4.desc',
      highlights: ['feature.highlight4.1', 'feature.highlight4.2', 'feature.highlight4.3'],
      reverse: true,
      image: esgSampleReport,
      image640: esgSampleReport640,
      width: 1280,
      height: 1634,
      smallImage: true
    },
    {
      icon: BarChart3,
      pillKey: 'pill.analytics',
      titleKey: 'featuresPage.feature6.title',
      descKey: 'featuresPage.feature6.desc',
      highlights: ['feature.highlight5.1', 'feature.highlight6.1', 'feature.highlight6.2'],
      reverse: false,
      image: kpisScreenshot,
      image640: kpisScreenshot640,
      width: 1280,
      height: 1125
    },
    {
      icon: Download,
      pillKey: 'pill.dataExport',
      titleKey: 'featuresPage.feature10.title',
      descKey: 'featuresPage.feature10.desc',
      highlights: ['feature.highlight10.1', 'feature.highlight10.3'],
      reverse: true,
      image: excelExportScreenshot,
      image640: excelExportScreenshot640,
      width: 1280,
      height: 689
    }
  ];

  const benefits = [
    t('benefits.item1'),
    t('benefits.item2'),
    t('benefits.item3'),
    t('benefits.item4'),
    t('benefits.item5')
  ];

  const stats = [
    { value: t('stats.companies'), label: t('stats.companiesLabel'), icon: Database },
    { value: t('stats.countries'), label: t('stats.countriesLabel'), icon: Globe2 },
    { value: t('stats.kpis'), label: t('stats.kpisLabel'), icon: Activity },
    { value: t('stats.years'), label: t('stats.yearsLabel'), icon: CalendarDays },
  ];

  return (
    <Layout>
      <section className="relative text-white overflow-hidden" style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6" data-testid="badge-hero">
                <Globe2 className="h-4 w-4 text-accent" />
                {t('hero.badge')}
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {t('hero.title1')}
                <span className="text-accent">{t('hero.title2')}</span>
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                {t('hero.description')}
              </p>
              <div className={`flex flex-col sm:flex-row gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                <Button variant="outline-white" size="xl" asChild>
                  <a
                    href="https://platform.esginvest.com/login"
                    data-testid="button-start-trial"
                    onClick={() => pushEvent('cta_start_trial', { location: 'hero' })}
                  >{t('hero.startTrial')}</a>
                </Button>
                <Button variant="outline-white" size="xl" asChild>
                  <a
                    href="#subscriptions"
                    data-testid="button-try-free"
                    onClick={(e) => {
                      e.preventDefault();
                      pushEvent('cta_subscribe', { location: 'hero_try_free' });
                      document.getElementById('subscriptions')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >{t('hero.tryFree')}</a>
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <MiddleEastMap isRTL={isRTL} />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-0 text-white overflow-hidden" style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center bottom' }}>
        <div className="absolute inset-0 bg-black/70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((stat, index) => (
              <div key={index} className="relative flex flex-col items-center justify-center px-6 py-12 group">
                <CountUp
                  value={stat.value}
                  testId={`text-stat-${index}`}
                  className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white mb-1 transition-colors duration-300 group-hover:text-accent tabular-nums"
                />
                <div className="w-8 h-[3px] bg-accent rounded-full mb-3 transition-all duration-300 group-hover:w-14" />
                <div className="text-xs font-semibold uppercase tracking-widest text-white/50 text-center">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TopFiveSection t={t} isRTL={isRTL} />

      <section className="py-16 bg-se-gray-50 dark:bg-background">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {featuresList.map((feature, index) => {
        const isEven = index % 2 === 0;
        const bgClass = isEven ? 'bg-white dark:bg-card' : 'bg-se-gray-50 dark:bg-background';
        
        const hasImage = !!feature.image;

        return (
          <section key={index} className={`py-20 ${bgClass}`}>
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
              <div className={`grid grid-cols-1 gap-12 items-center ${hasImage ? 'lg:grid-cols-2' : ''}`}>
                <div className={hasImage ? (feature.reverse !== isRTL ? 'lg:order-2' : '') : 'max-w-3xl mx-auto text-center'}>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent border border-accent/20 mb-4">
                    {t(feature.pillKey)}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    {t(feature.titleKey)}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    {t(feature.descKey)}
                  </p>
                  <ul className={hasImage ? 'space-y-3' : 'flex flex-wrap justify-center gap-x-8 gap-y-3'}>
                    {feature.highlights.map((highlightKey, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-se-green flex-shrink-0" />
                        <span className="text-foreground">{t(highlightKey)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {hasImage && (
                <div className={feature.reverse !== isRTL ? '' : 'lg:order-2'}>
                  {feature.image2 ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div
                        className="rounded-xl overflow-hidden border border-border shadow-md cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                        onClick={() => openLightbox(feature.image!, t(feature.titleKey))}
                        data-testid={`image-feature-${index}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') openLightbox(feature.image!, t(feature.titleKey)); }}
                      >
                        <img src={feature.image!} alt={t(feature.titleKey)} className="w-full h-auto" />
                      </div>
                      <div
                        className="rounded-xl overflow-hidden border border-border shadow-md cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                        onClick={() => openLightbox(feature.image2!, t(feature.titleKey))}
                        data-testid={`image-feature-${index}-2`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') openLightbox(feature.image2!, t(feature.titleKey)); }}
                      >
                        <img src={feature.image2} alt={t(feature.titleKey)} className="w-full h-auto" />
                      </div>
                    </div>
                  ) : (
                    <div className={feature.smallImage ? 'flex justify-center' : ''}>
                      <div
                        className="rounded-xl overflow-hidden border border-border shadow-md cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                        style={feature.smallImage ? { width: '60%' } : undefined}
                        onClick={() => openLightbox(feature.image!, t(feature.titleKey))}
                        data-testid={`image-feature-${index}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') openLightbox(feature.image!, t(feature.titleKey)); }}
                      >
                        <img
                          src={feature.image!}
                          srcSet={feature.image640 ? `${feature.image640} 640w, ${feature.image} 1280w` : undefined}
                          sizes={feature.smallImage ? '(max-width: 1024px) 60vw, 350px' : '(max-width: 1024px) 100vw, 590px'}
                          width={feature.width}
                          height={feature.height}
                          alt={t(feature.titleKey)}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-auto"
                        />
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-20 bg-white dark:bg-card">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-6">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent border border-accent/20">
              {t('pill.integration')}
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="lg:sticky lg:top-28">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t('integration.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-2">
                {t('integration.desc1')}
              </p>
              <p className="text-lg text-muted-foreground">
                {t('integration.desc2')}
              </p>
              <div className="mt-6">
                <p className="text-sm font-semibold text-foreground mb-3">
                  {t('integration.capabilities')}
                </p>
                <ul className="space-y-2.5">
                  {['integration.cap1', 'integration.cap2', 'integration.cap3', 'integration.cap4', 'integration.cap5', 'integration.cap6'].map((key) => (
                    <li key={key} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                      <CheckCircle className="h-4 w-4 text-se-green shrink-0 mt-0.5" />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { icon: Landmark, key: 'integration.step1' },
                { icon: Search, key: 'integration.step2' },
                { icon: Package, key: 'integration.step3' },
                { icon: Vote, key: 'integration.step4' },
                { icon: Cpu, key: 'integration.step5' },
              ].map((step, idx) => (
                <Card
                  key={idx}
                  className="group rounded-2xl p-5 bg-se-green-lighter dark:bg-accent/10 border border-accent/15 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-premium"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm transition-transform duration-200 group-hover:scale-105"
                      style={{ background: 'var(--gradient-accent)' }}
                    >
                      <step.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="pe-8">
                      <h3 className="font-semibold text-foreground mb-1 leading-snug">{t(`${step.key}.title`)}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{t(`${step.key}.desc`)}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-se-gray-50 dark:bg-background">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                {t('benefits.title')}
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                {t('benefits.subtitle')}
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3" data-testid={`text-benefit-${index}`}>
                    <CheckCircle className="h-5 w-5 text-se-green flex-shrink-0" />
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: Globe2, key: 'benefits.coverage', tone: 'cream' },
                { icon: Shield, key: 'benefits.methodology', tone: 'plain' },
                { icon: Zap, key: 'benefits.updates', tone: 'plain' },
                { icon: Users, key: 'benefits.support', tone: 'cream' },
              ].map(({ icon: Icon, key, tone }) => (
                <Card
                  key={key}
                  className={`group rounded-2xl p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-premium ${
                    tone === 'cream'
                      ? 'bg-se-green-lighter dark:bg-accent/10 border border-accent/15'
                      : 'bg-white dark:bg-card border border-border'
                  }`}
                >
                  <div
                    className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl shadow-sm transition-transform duration-200 group-hover:scale-105 ${
                      tone === 'cream' ? 'bg-white/80 dark:bg-card/60' : 'bg-se-green-lighter dark:bg-accent/10'
                    }`}
                  >
                    <Icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t(key)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(`${key}Desc`)}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="subscriptions" className="relative py-20 text-white overflow-hidden" style={{ backgroundImage: 'url(/subscriptions-hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/65 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-white/90 mb-8">
              {t('cta.subtitle')}
            </p>
          </div>
          <SubscriptionCards />
        </div>
      </section>
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white z-[10000]"
            onClick={closeLightbox}
            data-testid="button-close-lightbox"
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={lightboxImage}
            alt={lightboxAlt}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
        </div>
      )}
    </Layout>
  );
};

export default Index;
